import { FormEvent, useEffect, useRef, useState } from "react";
import { useAuth } from "./services/auth";
import { StatusMessage } from "./components/StatusMessage";
import { getStoredSignInEmail, hasEmailSignInLink } from "./services/firebase";

type LoginMode = "password" | "emailLink";

export function LoginPage() {
  const { loginWithPassword, requestLoginLink, completeLoginLink, devLogin, error, loading } = useAuth();
  const [isEmailLink] = useState(() => hasEmailSignInLink());
  const [storedEmail] = useState(() => isEmailLink ? getStoredSignInEmail() : "");
  const [email, setEmail] = useState(storedEmail);
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<LoginMode>("password");
  const [localError, setLocalError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const autoCompletionStarted = useRef(false);

  useEffect(() => {
    if (!isEmailLink || !storedEmail || autoCompletionStarted.current) return;
    autoCompletionStarted.current = true;
    setBusy(true);
    void completeLoginLink(storedEmail)
      .catch((err: unknown) => setLocalError(loginErrorMessage(err)))
      .finally(() => setBusy(false));
  }, [completeLoginLink, isEmailLink, storedEmail]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLocalError("");
    setNotice("");
    setBusy(true);
    try {
      if (isEmailLink) {
        await completeLoginLink(email.trim());
      } else if (mode === "password") {
        await loginWithPassword(email.trim(), password);
      } else {
        await requestLoginLink(email.trim());
        setNotice("Enviamos um link de acesso para seu e-mail. Verifique também o lixo eletrônico.");
      }
    } catch (err) {
      setLocalError(loginErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-box">
        <img src="/logo-mackenzie-ead.png" alt="Mackenzie EaD" />
        <h1>Engajamento Estudantil</h1>
        <p>
          {isEmailLink
            ? "Confirme o mesmo e-mail usado para solicitar este link."
            : mode === "password"
              ? "Entre com seu e-mail autorizado e sua senha."
              : "Informe seu e-mail autorizado. Enviaremos um link seguro para confirmar o acesso."}
        </p>
        <form onSubmit={(event) => void handleSubmit(event)}>
          <label className="field">
            <span>E-mail</span>
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              autoComplete="email"
              disabled={busy && Boolean(storedEmail)}
              required
            />
          </label>
          {!isEmailLink && mode === "password" ? (
            <label className="field">
              <span>Senha</span>
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type="password"
                autoComplete="current-password"
                required
              />
            </label>
          ) : null}
          <button className="button button--primary" type="submit" disabled={busy || loading}>
            {busy
              ? isEmailLink ? "Validando acesso..." : mode === "password" ? "Entrando..." : "Enviando link..."
              : isEmailLink ? "Concluir acesso" : mode === "password" ? "Entrar" : "Enviar link de acesso"}
          </button>
        </form>
        {!isEmailLink ? (
          <button
            className="button button--secondary"
            type="button"
            onClick={() => {
              setMode((current) => current === "password" ? "emailLink" : "password");
              setLocalError("");
              setNotice("");
            }}
            disabled={busy}
          >
            {mode === "password" ? "Entrar sem senha" : "Entrar com senha"}
          </button>
        ) : null}
        {import.meta.env.DEV ? (
          <button className="button button--secondary" type="button" onClick={devLogin} disabled={busy}>
            Entrar em modo local
          </button>
        ) : null}
        {notice ? <StatusMessage tone="success" title="Link enviado">{notice}</StatusMessage> : null}
        {busy && isEmailLink && storedEmail ? <StatusMessage>Validando seu link de acesso...</StatusMessage> : null}
        {localError || error ? <StatusMessage tone="danger">{localError || error}</StatusMessage> : null}
      </section>
    </main>
  );
}

function loginErrorMessage(error: unknown): string {
  const code = typeof error === "object" && error && "code" in error
    ? String((error as { code?: unknown }).code || "")
    : "";

  if (code === "auth/invalid-email") return "Informe um e-mail válido.";
  if (code === "auth/invalid-credential" || code === "auth/wrong-password" || code === "auth/user-not-found") {
    return "E-mail ou senha inválidos.";
  }
  if (code === "auth/user-disabled") return "Este usuário está desativado no Firebase.";
  if (code === "auth/invalid-action-code" || code === "auth/expired-action-code") {
    return "Este link expirou ou já foi utilizado. Solicite um novo link.";
  }
  if (code === "auth/unauthorized-continue-uri") {
    return "O endereço deste painel ainda não foi autorizado no Firebase. Procure a equipe CEDUC.";
  }
  if (code === "auth/operation-not-allowed") {
    return "O acesso sem senha ainda não foi habilitado no Firebase. Procure a equipe CEDUC.";
  }
  if (code === "auth/too-many-requests") {
    return "Muitas tentativas foram realizadas. Aguarde alguns minutos e tente novamente.";
  }
  return error instanceof Error ? error.message : "Não foi possível concluir o acesso.";
}
